import type { BlockId, StepId } from '../types/next'
import {
  getBlockNameByScope,
  isLayoutUiSchema,
  isNestedUiSchema
} from '../utils'
import type { BlockUiSchema, Journey, UiSchema, ZoneLayout } from '../utils'

/**
 * @deprecated will be sunset by journeys v4
 */
export const getMainContentCartZoneElements = (
  elements: UiSchema[][]
): ZoneLayout => {
  return [
    {
      zoneKey: 'content',
      elements: elements[0],
      order: 2
    },
    {
      zoneKey: 'side',
      elements: elements[1],
      order: 3
    },
    {
      zoneKey: 'header',
      elements: elements[2],
      order: 1
    },
    {
      zoneKey: 'footer',
      elements: elements[3],
      order: 4
    }
  ]
}

/**
 * Return immediate children of a UiSchema.
 *
 * NOTE: If the uischema is a zone layout, it will return zone elements as its immediate children.
 *
 * Tip: If you want to return all nested children regardless of zones, use {@link getNestedUiSchemaElements} instead.
 *
 * @deprecated will be sunset by journeys v4
 */
export const getUiSchemaElements = (uischema: UiSchema | undefined) => {
  return uischema
    ? isNestedUiSchema(uischema)
      ? getMainContentCartZoneElements(uischema.elements)
      : isLayoutUiSchema(uischema)
        ? uischema.elements
        : []
    : []
}

/**
 * Return all nested children of a UiSchema, including the groups themselves.
 *
 * Tip: If you want to return only immediate children, use {@link getUiSchemaElements} instead.
 *
 * @deprecated will be sunset by journeys v4
 */
export const getNestedUiSchemaElements = (
  uischema: UiSchema | undefined
): BlockUiSchema[] => {
  if (uischema) {
    if (isNestedUiSchema(uischema)) {
      return uischema.elements.flatMap((elements) =>
        elements.flatMap((uischema) =>
          [uischema].concat(...getNestedUiSchemaElements(uischema))
        )
      )
    } else if (isLayoutUiSchema(uischema)) {
      return uischema.elements.flatMap((uischema) => {
        if (isLayoutUiSchema(uischema)) {
          return [uischema, ...getNestedUiSchemaElements(uischema)]
        }

        return [uischema]
      })
    }

    return []
  }

  return []
}

/**
 * Parse throught all nested children of a UiSchema, including the groups themselves,
 * returning their block names.
 *
 * @deprecated will be sunset by journeys v4
 */
export const getNestedUiSchemaElementNames = (
  uischema: UiSchema | undefined
): string[] => {
  return getNestedUiSchemaElements(uischema).reduce<string[]>(
    (acc, element) => {
      const name = getBlockNameByScope(element.scope)

      if (name !== undefined) acc.push(name)

      return acc
    },
    []
  )
}

export type BlockIdsMap = Map<
  BlockId | string,
  { stepId: StepId; stepIndex: number; stepName: string; blockName: string }
>

/**
 * Parse throught all nested children of a UiSchema, including the groups themselves,
 * returning their block names.
 *
 * @deprecated will be sunset by journeys v4
 *
 * TODO: unit test
 */
export const computeBlockIdsMap = (
  steps: Journey['steps'] | undefined
): BlockIdsMap => {
  const map = new Map()

  if (!steps) return map

  for (const [stepIndex, step] of steps.entries()) {
    const blocks = getNestedUiSchemaElements(step.uischema)

    for (const block of blocks) {
      if (block.id) {
        map.set(block.id, {
          stepId: step.stepId,
          stepIndex: stepIndex,
          stepName: step.name,
          blockName: getBlockNameByScope(block.scope)
        })
      }
    }
  }

  return map
}
